import { InjectionKeys } from '@/constants';
import { cloneDeep } from 'lodash';
import { computed, provide, ref, Ref, watch } from 'vue';

/**
 * Provides state that handles both object creation and editing.
 * @param propsRef Reference to prop value that is expected to be edited. Ensure that value is wrapper with toRef() to preserve reactivity.
 * @param formDefaultsValue Default values for a newly created object.
 * @param storeUpdate The store function that will update an object with its new values.
 * @param storeCreate The store function that creates an object.
 * @returns The state to pass to a form for property setting.
 */
export const useAddEditFormState = <T, U extends Omit<T, 'id'>>(
  propsRef: Ref<T | undefined>,
  formDefaultsValue: U,
  storeUpdate: (value: T) => Promise<void>,
  storeCreate: (value: U) => Promise<void>
) => {
  // Edits use propsRef and adds use formDefaultsValue
  const propsOrFormDefaults = (): T | U => cloneDeep(propsRef.value ?? formDefaultsValue);

  const state = ref(propsOrFormDefaults()) as Ref<T | U>;
  const setPropsOrFormDefaults = () => {
    state.value = propsOrFormDefaults();
  };

  watch(propsRef, () => setPropsOrFormDefaults());

  const submit = async () => {
    if (propsRef.value) {
      await storeUpdate(state.value as T);
    } else {
      await storeCreate(state.value as U);
    }

    return () => setPropsOrFormDefaults();
  };

  provide(InjectionKeys.FormSubmit, submit);
  provide(InjectionKeys.FormCancel, setPropsOrFormDefaults);

  return state;
};

/**
 * Returns 'Edit' if true, and 'Add' otherwise.
 * @param condition A truthy or falsy value.
 * @returns The string 'Edit' or 'Add'
 */
export const addOrEditHeader = (condition: Ref<unknown>) => computed(() => (condition.value ? 'Edit' : 'Add'));

/**
 * Provides modal and open state and functions to toggle the open state.
 * @returns An array with the modal state, modal open state, a function to open the modal, and a function to close the modal.
 */
export const useModalState = <T>(): [Ref<T | undefined>, Ref<boolean>, (value?: T) => void] => {
  const modalState = ref<T | undefined>();
  const modalOpenState = ref<boolean>(false);

  const openModal = (value?: T) => {
    modalState.value = value;
    modalOpenState.value = true;
  };

  return [modalState, modalOpenState, openModal];
};
